import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { dayKey } from './useHabits'
import { generateSteps, currentStepFor, adherenceScore } from '../components/sleep/logic'

// Sleep Realignment data layer: the user's active plan (+ its steps),
// today's log, and the computed "current step / today's targets".
// Self-scoped per user.
export function useSleep() {
  const { user } = useAuth()
  const userId = user?.id || null

  const [plan, setPlan] = useState(null)
  const [steps, setSteps] = useState([])
  const [todayLog, setTodayLog] = useState(null)
  const [recentLogs, setRecentLogs] = useState([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    if (!userId) { setPlan(null); setSteps([]); setTodayLog(null); setLoading(false); return }
    setLoading(true)
    const { data: plans } = await supabase
      .from('sleep_plans')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['active', 'maintenance'])
      .order('created_at', { ascending: false })
      .limit(1)
    const p = plans?.[0] || null
    setPlan(p)
    if (!p) { setSteps([]); setTodayLog(null); setRecentLogs([]); setLoading(false); return }

    const [{ data: st }, { data: logs }] = await Promise.all([
      supabase.from('sleep_plan_steps').select('*').eq('plan_id', p.id).order('step_no'),
      supabase.from('sleep_logs').select('*').eq('plan_id', p.id).order('day', { ascending: false }).limit(60),
    ])
    setSteps(st || [])
    setRecentLogs(logs || [])
    setTodayLog((logs || []).find((l) => l.day === dayKey()) || null)
    setLoading(false)
  }, [userId])

  useEffect(() => { refetch() }, [refetch])

  // The step (and target times) that apply today. The sleep-advance cron
  // is authoritative once it runs (it bakes adaptive holds into
  // current_step_no), so trust the denormalized step; fall back to the
  // pure linear computation for the first day before any cron tick.
  const startKey = plan ? (plan.step_started_on || dayKey(new Date(plan.created_at))) : null
  const currentStep = useMemo(() => {
    if (!plan || !steps.length) return null
    return steps.find((s) => s.step_no === plan.current_step_no)
      || currentStepFor(steps, startKey, dayKey())
  }, [plan, steps, startKey])

  // Create a plan from the setup form: insert the plan, generate +
  // insert its steps, stamp the current step. Returns the plan id.
  const createPlan = useCallback(async (form) => {
    if (!userId) return null
    const today = dayKey()
    const { data: inserted, error } = await supabase
      .from('sleep_plans')
      .insert({
        user_id: userId,
        start_bedtime: form.start_bedtime,
        start_waketime: form.start_waketime,
        target_bedtime: form.target_bedtime,
        target_waketime: form.target_waketime,
        shift_increment: form.shift_increment,
        hold_days_per_step: form.hold_days_per_step,
        melatonin_offset: form.melatonin_offset,
        melatonin_dose: form.melatonin_dose,
        status: 'active',
        current_step_no: 1,
        step_started_on: today,
      })
      .select('*')
      .single()
    if (error || !inserted) return null

    const gen = generateSteps(inserted)
    if (gen.length) {
      await supabase.from('sleep_plan_steps').insert(
        gen.map((s) => ({ ...s, plan_id: inserted.id, user_id: userId })),
      )
      const first = gen[0]
      await supabase.from('sleep_plans').update({
        current_bed_target: first.bed_target,
        current_wake_target: first.wake_target,
        current_melatonin_time: first.melatonin_time,
      }).eq('id', inserted.id)
    }
    await refetch()
    return inserted.id
  }, [userId, refetch])

  // Upsert today's log with a patch (checklist toggles, actual times).
  const logToday = useCallback(async (patch) => {
    if (!userId || !plan) return
    const day = dayKey()
    const merged = { ...(todayLog || {}), ...patch }
    const score = adherenceScore(merged, currentStep?.bed_target)
    setTodayLog({ ...merged, day, adherence_score: score }) // optimistic
    await supabase.from('sleep_logs').upsert(
      {
        plan_id: plan.id, user_id: userId, day,
        actual_bedtime: merged.actual_bedtime ?? null,
        actual_waketime: merged.actual_waketime ?? null,
        melatonin_taken: !!merged.melatonin_taken,
        melatonin_time: merged.melatonin_time ?? null,
        morning_light: !!merged.morning_light,
        wind_down: !!merged.wind_down,
        sleep_quality: merged.sleep_quality ?? null,
        adherence_score: score,
      },
      { onConflict: 'plan_id,day' },
    )
    await refetch()
  }, [userId, plan, todayLog, currentStep, refetch])

  // Abandon the active plan (back to setup).
  const endPlan = useCallback(async () => {
    if (!plan) return
    await supabase.from('sleep_plans').update({ status: 'complete' }).eq('id', plan.id)
    await refetch()
  }, [plan, refetch])

  // Days from now until the final (target) step lands.
  const daysToTarget = useMemo(() => {
    if (!plan || !steps.length || !currentStep) return null
    const remainingSteps = steps.filter((s) => s.step_no >= currentStep.step_no)
    return remainingSteps.reduce((sum, s) => sum + s.hold_days, 0)
  }, [plan, steps, currentStep])

  return { plan, steps, currentStep, todayLog, recentLogs, daysToTarget, loading, createPlan, logToday, endPlan, refetch }
}
